import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpErrorResponse, HttpEventType } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';

// Interfaces para tipado de respuestas
export interface LoginResponse {
  token: string;
  abilities: string[];
  user: {
    id: string;
    email: string;
    name: string | null;
    accountId: string;
    roleId: string;
    isFirstLogin: boolean;
    isActive: boolean;
    profileImageUrl: string;
    role: {
      id: string;
      name: string;
      slug: string;
      description: string;
    };
  };
  account: {
    id: string;
    name: string;
    slug: string;
    email: string;
    logoUrl: string;
  };
  client: {
    id: string;
    name: string;
    surname: string;
    nif: string;
    wristbands: any[];
  };
  clientId: string;
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface UploadResponse {
  success: boolean;
  message: string;
  data?: any;
}

export interface UploadProgress {
  progress: number;
  status: 'uploading' | 'completed' | 'error';
  response?: UploadResponse;
}

/**
 * Servicio centralizado para todas las llamadas HTTP a la API.
 * 
 * Responsabilidades:
 * - Construcción de URLs de endpoints
 * - Configuración de headers (autenticación, content-type)
 * - Manejo consistente de errores HTTP
 * - Logging de requests/responses
 * - Tipado de respuestas
 * 
 * Uso:
 * Inyectar este servicio en lugar de HttpClient directamente.
 * Esto facilita el mantenimiento, testing y cambios de API.
 */
@Injectable({
  providedIn: 'root'
})
export class ApiService {
  // Configuración del entorno
  private readonly protocol: string;
  private readonly server: string;
  private readonly apiUrl: string;
  private readonly apiRoute: string;

  constructor(private http: HttpClient) {
    this.protocol = environment.protocol;
    this.server = environment.server;
    this.apiUrl = environment.apiUrl;
    this.apiRoute = `${this.protocol}${this.server}${this.apiUrl}`;
    
  }

  /**
   * Obtiene los headers por defecto para las peticiones.
   * @param token Token de autenticación (opcional)
   * @param contentType Content-Type (opcional, por defecto 'application/json')
   */
  private getHeaders(token?: string | null, contentType?: string): HttpHeaders {
    let headers = new HttpHeaders();
    
    if (token) {
      headers = headers.set('Authorization', `Bearer ${token}`);
    }
    
    if (contentType) {
      headers = headers.set('Content-Type', contentType);
    }
    
    return headers;
  }

  /**
   * Maneja errores HTTP de forma centralizada.
   * @param error Error HTTP
   * @param context Contexto adicional para logging
   */
  private handleError(error: HttpErrorResponse, context: string): Observable<never> {
    console.error(`[ApiService] Error en ${context}:`, {
      status: error.status,
      statusText: error.statusText,
      message: error.message,
      error: error.error,
      url: error.url
    });

    let errorMessage = 'Error desconocido';

    switch (error.status) {
      case 0:
        errorMessage = 'No se puede conectar con el servidor. Verifica tu conexión a internet.';
        break;
      case 401:
        errorMessage = 'No autorizado. Token inválido o expirado.';
        break;
      case 403:
        errorMessage = 'No tienes permisos para acceder a este recurso.';
        break;
      case 404:
        errorMessage = 'Recurso no encontrado.';
        break;
      case 422:
        errorMessage = 'Datos de entrada inválidos.';
        break;
      case 500:
        errorMessage = 'Error interno del servidor. Inténtalo más tarde.';
        break;
      default:
        errorMessage = `Error ${error.status}: ${error.statusText || error.message}`;
    }

    return throwError(() => new Error(errorMessage));
  }

  // ============================================================================
  // ENDPOINTS DE AUTENTICACIÓN
  // ============================================================================

  /**
   * Login de usuario.
   * POST /login
   */
  login(email: string, password: string): Observable<LoginResponse> {
    const loginData: LoginRequest = { email, password };
    const headers = this.getHeaders(null, 'application/json');
    const url = `${this.apiRoute}login`;


    return this.http.post<LoginResponse>(url, loginData, { headers }).pipe(
      tap(_response => {}),
      catchError(error => this.handleError(error, 'login'))
    );
  }

  /**
   * Renovación de token (re-login con credenciales guardadas).
   * POST /login
   */
  refreshToken(email: string, password: string): Observable<LoginResponse> {
    const loginData: LoginRequest = { email, password };
    const headers = this.getHeaders(null, 'application/json');
    const url = `${this.apiRoute}login`;


    return this.http.post<LoginResponse>(url, loginData, { headers }).pipe(
      tap(_response => {}),
      catchError(error => this.handleError(error, 'refreshToken'))
    );
  }

  // ============================================================================
  // ENDPOINTS DE EVENTOS
  // ============================================================================

  /**
   * Obtiene los eventos de un usuario.
   * GET /accounts/{accountId}/eventUser?query={query}&userId={userId}
   */
  getEventUser(accountId: string, userId: string, token: string, query = ''): Observable<any> {
    const headers = this.getHeaders(token, 'application/json');
    const url = `${this.apiRoute}accounts/${accountId}/eventUser?query=${query}&userId=${userId}`;


    return this.http.get(url, { headers }).pipe(
      tap(response => {}),
      catchError(error => this.handleError(error, 'getEventUser'))
    );
  }

  // ============================================================================
  // ENDPOINTS DE IMPORTACIÓN FÍSICA
  // ============================================================================



  /**
   * Versión simplificada usando fetch API directamente (compatible con el código existente).
   * POST /accounts/{accountId}/events/{eventId}/physicalImport
   * 
   * @param accountId ID de la cuenta
   * @param eventId ID del evento
   * @param file Archivo a subir
   * @param token Token de autenticación
   * @returns Promise<Response>
   */
  async uploadPhysicalImportFetch(
    accountId: string,
    eventId: string,
    file: File,
    token: string
  ): Promise<Response> {
    const formData = new FormData();
    formData.append('file', file, file.name);
    
    const url = `${this.apiRoute}accounts/${accountId}/events/${eventId}/physicalImport`;

    

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`
      },
      body: formData,
      credentials: 'same-origin',
      mode: 'cors'
    });

    return response;
  }

  /**
   * Versión simplificada usando fetch API para saveAssociatedWithExcel.
   * POST /accounts/{accountId}/events/{eventId}/saveAssociatedWithExcel
   * 
   * @param accountId ID de la cuenta
   * @param eventId ID del evento
   * @param file Archivo procesado a subir
   * @param token Token de autenticación
   * @returns Promise<Response>
   */
  async saveAssociatedWithExcelFetch(
    accountId: string,
    eventId: string,
    file: File,
    token: string
  ): Promise<Response> {
    const formData = new FormData();
    formData.append('file', file, file.name);
    
    const url = `${this.apiRoute}accounts/${accountId}/events/${eventId}/saveAssociatedWithExcel`;

    
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`
      },
      body: formData,
      credentials: 'same-origin',
      mode: 'cors'
    });

    return response;
  }

  searchOtherWristbands(
    accountId: string,
    eventId: string,
    token: string,
    body: any
  ): Observable<any> {
    const headers = this.getHeaders(token, 'application/json');
    const url = `${this.apiRoute}accounts/${accountId}/events/${eventId}/searchOtherWristbands`;
    // Enviar la lista de pulseras en el body como JSON
    return this.http.post(url, body, { headers }).pipe(
      tap(_response => {}),
      catchError(error => this.handleError(error, 'searchOtherWristbands'))
    );
  }

  activateorDeactivateWristband(
    accountId: string,
    eventId: string,
    wristbandId: string,
    token: string
  ){
    const headers = this.getHeaders(token, 'application/json');
    const url = `${this.apiRoute}accounts/${accountId}/events/${eventId}/wristbands/${wristbandId}/activateOrDeactivate`;


    return this.http.patch(url, {}, { headers }).pipe(
      tap(_response => {}),
      catchError(error => this.handleError(error, 'activateorDeactivateWristband'))
    );
  }

  // ============================================================================
  // UTILIDADES
  // ============================================================================

  /**
   * Obtiene la URL base de la API.
   */
  getApiRoute(): string {
    return this.apiRoute;
  }

  /**
   * Prueba la conexión al servidor.
   * GET /accounts (endpoint básico)
   */
  testConnection(token: string): Observable<any> {
    const headers = this.getHeaders(token, 'application/json');
    const url = `${this.apiRoute}accounts`;


    return this.http.get(url, { headers }).pipe(
      tap(_response => {}),
      map(response => ({ success: true, message: 'Conexión exitosa', data: response })),
      catchError(error => this.handleError(error, 'testConnection'))
    );
  }
}
